// Sales Order page pieces for Time & Materials orders (slice 1d). Each renders NOTHING for a
// fixed-price order (null / 'fixed' pricing_mode), so a fixed order's page is unchanged.
import React from 'react';
import { fmtMoney } from '../../utils/priceMath';
import { activeInvoices, isTm, isTmFinal, isTmPending } from '../../utils/tmPricing';

const fmtWhen = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

/** "T&M" badge beside the order's status. */
export function TmSoStatusBadge({ header }) {
  if (!isTm(header)) return null;
  return (
    <span className={`badge ${isTmFinal(header) ? 'bg-success' : 'bg-info text-dark'} ms-2`} style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}
      title={isTmFinal(header) ? 'Time & Materials: priced' : 'Time & Materials: pricing pending'}>
      T&amp;M
    </span>
  );
}

/**
 * Header banner of a T&M order.
 *   pending → blue: rates come from the quote, the internal estimate / not-to-exceed, [Approve T&M Pricing]
 *   final   → green: priced by / at / run #, [Reopen T&M pricing] only while no invoice exists;
 *             once invoiced: "Revise T&M pricing: call Mike".
 */
export function TmSoBanner({ header, related, onApprove, onReopen, blockedReason = null }) {
  if (!isTm(header)) return null;
  if (isTmPending(header)) {
    return (
      <div className="alert alert-info m-3 mb-0 d-flex align-items-center gap-3" data-testid="tm-so-banner">
        <i className="bi bi-calculator fs-3"></i>
        <div className="flex-grow-1">
          <strong>Time &amp; Materials order — pricing pending</strong>
          <div className="small">
            Priced from this order&apos;s own shop time and material at the quote&apos;s rates
            {header.quote_id ? ` (Quote #${header.quote_id})` : ''} once every good piece has shipped. Lines read $0.00 until then.
            {(header.tm_estimate_total != null || header.tm_not_to_exceed != null) && (
              <span className="ms-1">
                Internal notes:
                {header.tm_estimate_total != null && <> est. {fmtMoney(header.tm_estimate_total)}</>}
                {header.tm_not_to_exceed != null && <> · not-to-exceed {fmtMoney(header.tm_not_to_exceed)}</>}
              </span>
            )}
          </div>
        </div>
        <button type="button" className="btn btn-primary btn-sm text-nowrap" onClick={onApprove}
          disabled={!!blockedReason} title={blockedReason || undefined}>
          <i className="bi bi-calculator me-1"></i>Approve T&amp;M Pricing
        </button>
      </div>
    );
  }
  const inv = activeInvoices(related)[0] || null;
  return (
    <div className="alert alert-success m-3 mb-0 d-flex align-items-center gap-3" data-testid="tm-so-banner">
      <i className="bi bi-check2-circle fs-3"></i>
      <div className="flex-grow-1">
        <strong>Time &amp; Materials order — priced</strong>
        <div className="small">
          Approved{header.tm_priced_by ? ` by ${header.tm_priced_by}` : ''}{header.tm_priced_at ? ` on ${fmtWhen(header.tm_priced_at)}` : ''}
          {header.tm_pricing_run_id ? ` (run #${header.tm_pricing_run_id})` : ''}. Lines are frozen.
          {inv
            ? <> Invoiced (#{inv.invoice_number}). To revise T&amp;M pricing, call Mike.</>
            : <> Create Invoice makes the one close-out invoice.</>}
        </div>
      </div>
      {!inv && (
        <button type="button" className="btn btn-outline-warning btn-sm text-nowrap" onClick={onReopen}
          disabled={!!blockedReason} title={blockedReason || undefined}>
          <i className="bi bi-arrow-counterclockwise me-1"></i>Reopen T&amp;M pricing
        </button>
      )}
    </div>
  );
}

/** List chip: "T&M · pending" / "T&M · priced". */
export function TmOrderChip({ order, selected }) {
  if (!isTm(order)) return null;
  const pending = isTmPending(order);
  return (
    <span
      className={`badge ${selected ? 'bg-light text-dark' : (pending ? 'bg-info text-dark' : 'bg-success')} ms-2`}
      style={{ fontSize: '0.6rem', verticalAlign: 'middle' }}
      title={pending ? 'Time & Materials: pricing pending' : 'Time & Materials: priced'}
    >
      T&amp;M · {pending ? 'pending' : 'priced'}
    </span>
  );
}

/** Replaces the revenue / profit / margin band while a T&M order is unpriced (there is no revenue yet). */
export function TmProductionNote() {
  return (
    <div className="row text-center align-items-center" data-testid="tm-production-note">
      <div className="col">
        <div className="text-uppercase small fw-bold mb-1 opacity-75">Revenue &amp; margin</div>
        <div className="small">
          Time &amp; Materials: revenue and margin are set when the order is priced (Approve T&amp;M Pricing).
          The costs above are what the order has used so far.
        </div>
      </div>
    </div>
  );
}

/** Note under the Line Items header of a priced T&M order (Edit Items is hidden). */
export function TmLinesLockNote({ header }) {
  if (!isTmFinal(header)) return null;
  return (
    <span className="small text-muted" title="A priced Time & Materials order's lines are frozen.">
      <i className="bi bi-lock-fill me-1"></i>Priced T&amp;M order: lines are frozen (Reopen T&amp;M pricing before invoicing to change them).
    </span>
  );
}

/** Price cell of a NEW line on a pending T&M order: no price input, it is priced at completion. */
export function TmNewLinePrice() {
  return (
    <div className="text-muted small" title="Time & Materials: new lines are added at $0.00 and priced with the rest of the order.">
      T&amp;M
      <div style={{ fontSize: '0.65rem' }}>priced at completion</div>
    </div>
  );
}
